import { inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { MycoplasmaTypes } from '@enums/mycoplasma-types';
import { MycoplasmaService } from '@services/mycoplasma.service';
import { NavigationService } from '@services/navigation.service';
import { OrderService } from '@services/order.service';
import { StorageService } from '@services/storage.service';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class MycoplasmaUpsellGuard implements CanActivate {
  private router = inject(Router);
  private mycoplasmaService = inject(MycoplasmaService);
  private orderService = inject(OrderService);
  private navigationService = inject(NavigationService);
  private storageService = inject(StorageService);

  /**
   * Determines whether a Mycoplasma upsell page can be activated.
   *
   * @param {ActivatedRouteSnapshot} route the route to be activated
   * @param {RouterStateSnapshot} state the current router state
   */
  public canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> {
    const mycoplasmaType = route.data['mycoplasmaType'] as MycoplasmaTypes;

    if (this.shouldBlockAccess(mycoplasmaType)) {
      return this.redirectToNextPage(state.url);
    }

    return of(true);
  }

  /**
   * Checks if access to the Mycoplasma upsell should be blocked based on the page type.
   *
   * @param {MycoplasmaTypes} mycoplasmaType the type of Mycoplasma page
   *
   * @returns {boolean} true if access should be blocked, false otherwise
   */
  private shouldBlockAccess(mycoplasmaType: MycoplasmaTypes): boolean {
    switch (mycoplasmaType) {
      case MycoplasmaTypes.Genitalium:
        return this.mycoplasmaService.isMycoplasmaGenitaliumTestOrdered();
      case MycoplasmaTypes.HominisUreaplasma:
        return (
          !this.mycoplasmaService.isMycoplasmaGenitaliumTestOrdered() ||
          this.mycoplasmaService.isMycoplasmaHominisWithUreaplasmaTestOrdered()
        );
      default:
        return false;
    }
  }

  /**
   * Redirects to the next page in the ordering workflow.
   *
   * @param {string} currentUrl the URL currently being navigated to
   *
   * @returns {Observable<UrlTree>} an observable that emits a UrlTree for redirection
   */
  private redirectToNextPage(currentUrl: string): Observable<UrlTree> {
    return this.orderService.showOrderBetterLab().pipe(
      map((response) => {
        this.storageService.betterLab = !!response.has_better_lab;

        return this.createRedirectUrlTree(currentUrl);
      }),
      catchError(() => of(this.createRedirectUrlTree(currentUrl))),
    );
  }

  /**
   * Creates a UrlTree for redirecting to the next navigation URL.
   *
   * @param {string} currentUrl the URL currently being navigated to
   *
   * @returns {UrlTree} a UrlTree for the next page in the workflow
   */
  private createRedirectUrlTree(currentUrl: string): UrlTree {
    return this.router.createUrlTree([this.navigationService.getNextNavigationUrl(currentUrl)]);
  }
}
